import { Common, color } from '@stockbit/ui';
import numeral from 'numeral';
import { chartTimeframes, priceChange } from 'features/company/constants';
import { useCallback, useMemo } from 'react';
import { parseCountAbbrv2 } from 'utils/general';
import { CompanyChartData } from 'lib/entities/company.types';
import {
  CustomIconmoon,
  ResponsiveText,
  TimeframeButtonsMobile,
  TimeText,
  ChartSectionItem,
} from './styled';
import ExpandButtonToggle from './ExpandButtonToggle';
import { ChartTimeFrames } from '../constants.types';

const { Flex, Text } = Common;

interface ChartDetailProps {
  onExpand: () => void;
  symbol: string;
  timeframe: ChartTimeFrames;
  showTimeframes: boolean;
  company: any;
  chart?: CompanyChartData;
  onSelectTimeframe: (v: ChartTimeFrames) => void;
}

function ChartDetail({
  timeframe: stateTimeframe,
  showTimeframes,
  company = {},
  chart,
  onSelectTimeframe,
  onExpand,
}: ChartDetailProps) {
  const {
    time = '',
    change: companyChange,
    percentage: companyPercentage,
    price,
    volume,
    average,
  } = company;
  const timeframe = chart?.timeframe ?? stateTimeframe;
  const isTimeframeToday = timeframe === 'today';
  const change = isTimeframeToday ? companyChange : chart?.change;
  const percentage = isTimeframeToday ? companyPercentage : chart?.percentage;

  const renderTime = useCallback(() => {
    if (timeframe === 'today') {
      const timeOnly = time.slice(4);
      return (
        <>
          Today <time dateTime={`${timeOnly}+07:00`}>{`${time} WIB`}</time>
        </>
      );
    }
    if (timeframe === 'ytd') {
      return 'Year to Date';
    }
    if (timeframe[1] === 'w') {
      return `Past ${timeframe[0]} Week`;
    }
    if (timeframe[1] === 'm') {
      return `Past ${timeframe[0]} Month`;
    }
    if (timeframe[1] === 'y') {
      return `Past ${timeframe[0]} Year`;
    }

    return '';
  }, [timeframe, time]);

  const numeralChange = useMemo(() => numeral(change), [change]);

  const priceType = useMemo(() => {
    if (numeralChange.value() > 0) return 'up';
    if (numeralChange.value() < 0) return 'down';
    return 'netral';
  }, [numeralChange]);
  const formattedPrice = useMemo(
    () => numeral(price).format('0,0.[00]'),
    [price],
  );
  const formattedChange = useMemo(() => {
    const val = numeralChange.value();
    if (isNaN(val)) return 'NA';
    if (val === 0) return '0.00';
    return numeralChange.format('+0,0.[00]');
  }, [numeralChange]);
  const formattedPercentage = useMemo(
    () => numeral(percentage || 0).format('0.00'),
    [percentage],
  );
  const formattedVolume = useMemo(
    () => (isNaN(volume) ? 'NA' : parseCountAbbrv2(volume)),
    [volume],
  );
  const formattedAvgVolume = useMemo(
    () =>
      isNaN(Number(average))
        ? 'NA'
        : parseCountAbbrv2(parseInt(String(average), 10)),
    [average],
  );
  const isNAorZero = useMemo(
    () => ['NA', '0', '0.00'].includes(String(formattedAvgVolume)),
    [formattedAvgVolume],
  );

  const renderPriceTime = () => (
    <div>
      <Flex align="center">
        <ResponsiveText as="h3" family="bold" margin="0px" padding="0px">
          {formattedPrice}
        </ResponsiveText>
        <CustomIconmoon
          as="span"
          className={priceChange?.[priceType]?.icon}
          color={priceChange?.[priceType]?.color}
          hoverColor={priceChange?.[priceType]?.color}
        />
        <ResponsiveText as="h3" color={priceChange?.[priceType]?.color} px="5px">
          {formattedChange}
        </ResponsiveText>
        <ResponsiveText as="h3" color={priceChange?.[priceType]?.color}>
          {`(${formattedPercentage}%)`}
        </ResponsiveText>
      </Flex>
      <TimeText as="span" color={color.midgray} size="12px" pt="3px">
        {renderTime()}
      </TimeText>
    </div>
  );

  const renderVolume = () => (
    <ChartSectionItem>
      <Text as="h3" size="16px">
        {formattedVolume}
      </Text>
      <Text color={color.midgray} size="12px" pt="3px">
        Volume
      </Text>
    </ChartSectionItem>
   );

  const renderAvgVolume = () => (
    <ChartSectionItem>
      <Text as="h3" size="16px">
        {formattedAvgVolume}
      </Text>
      <Text color={color.midgray} size="12px" pt="3px">
        Avg volume
      </Text>
    </ChartSectionItem>
  );

  const renderTimeframeButtonsMobile = () => (
    <TimeframeButtonsMobile
      showTimeframes={showTimeframes}
      timeframe={timeframe}
      onSelectTimeframe={onSelectTimeframe}
      timeFrameList={chartTimeframes}
    />
  );

  const renderExpandButtonToggle = () => <ExpandButtonToggle onExpand={onExpand} />;

  return (
    <Flex align="center" justify="space-between">
      <Flex>
        {renderPriceTime()}
        {renderVolume()}
        {!isNAorZero && renderAvgVolume()}
      </Flex>
      {renderTimeframeButtonsMobile()}
      {renderExpandButtonToggle()}
    </Flex>
  );
}

export default ChartDetail;
